/**
 * Site Detail Page - Displays detailed capacity breakdown and scenario modeling
 * Requirements: 2.1, 2.4
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loadSiteDetail, clearSelectedSite } from '../store/slices/sitesSlice';
import { clearScenario } from '../store/slices/scenarioSlice';
import CapacityTable from '../components/CapacityTable';
import SiteCapabilities from '../components/SiteCapabilities';
import CostSummary from '../components/CostSummary';
import ScenarioPanel from '../components/ScenarioPanel';
import { InsightsPanel } from '../components/InsightsPanel';
import { exportSitePDF } from '../services/api';

function SiteDetail() {
  const { siteId } = useParams<{ siteId: string }>();
  const dispatch = useAppDispatch();
  const { selectedSiteDetail, loading, error } = useAppSelector((state) => state.sites);
  const scenarioState = useAppSelector((state) => state.scenario);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (siteId) {
      dispatch(loadSiteDetail(siteId));
    }
    return () => {
      dispatch(clearSelectedSite());
      dispatch(clearScenario());
    };
  }, [dispatch, siteId]);

  const handleExportPDF = useCallback(async (includeScenario: boolean = false) => {
    if (!selectedSiteDetail) return;
    
    setExporting(true);
    try {
      const blob = await exportSitePDF({
        siteId: selectedSiteDetail.site.siteId,
        includeScenario,
        scenario: includeScenario && scenarioState.calculatedMetrics ? {
          adjustments: scenarioState.adjustments,
          metrics: scenarioState.calculatedMetrics,
        } : undefined,
      });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedSiteDetail.site.name.replace(/\s+/g, '_')}_Report.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [selectedSiteDetail, scenarioState]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading site details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>Error: {error}</div>
        <Link to="/" style={styles.backLink}>← Back to Dashboard</Link>
      </div>
    );
  }

  if (!selectedSiteDetail) {
    return (
      <div style={styles.container}>
        <div style={styles.notFound}>Site not found</div>
        <Link to="/" style={styles.backLink}>← Back to Dashboard</Link>
      </div>
    );
  }

  const { site, capacity, costParameters } = selectedSiteDetail;


  // Calculate overall utilization
  const totalPlannedCases = capacity.reduce((sum, row) => sum + row.plannedCases, 0);
  const totalLoadCapacity = capacity.reduce((sum, row) => sum + (row.loadCapacity || 0), 0);
  const overallUtilization = totalLoadCapacity > 0 ? totalPlannedCases / totalLoadCapacity : 1;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <Link to="/" style={styles.backLink}>← Back to Dashboard</Link>
          <div style={styles.exportButtons}>
            <button
              onClick={() => handleExportPDF(false)}
              disabled={exporting}
              style={styles.exportButton}
            >
              {exporting ? 'Exporting...' : '📄 Export PDF'}
            </button>
            {scenarioState.calculatedMetrics && (
              <button
                onClick={() => handleExportPDF(true)}
                disabled={exporting}
                style={styles.exportButtonSecondary}
              >
                📊 Export with Scenario
              </button>
            )}
          </div>
        </div>
        <div style={styles.titleRow}>
          <h1 style={styles.title}>{site.name}</h1>
          <span style={styles.location}>{site.location}</span>
        </div>
        <div style={styles.utilizationBadge}>
          Overall Utilization: {(overallUtilization * 100).toFixed(1)}%
        </div>
      </div>

      {/* Single Column Layout - All sections full width */}
      <div style={styles.mainContent}>
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Capacity Breakdown</h2>
          <CapacityTable capacity={capacity} />
        </section>

        {/* Two-column row for Site Capabilities and Cost Summary */}
        <div style={styles.twoColumnRow}>
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Site Capabilities</h2>
            <SiteCapabilities capabilities={site.capabilities} />
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Cost Summary</h2>
            <CostSummary costParameters={costParameters} totalCases={totalPlannedCases} />
          </section>
        </div>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Scenario Modeling</h2>
          <ScenarioPanel
            siteId={site.siteId}
            capacity={capacity}
            fixedCost={costParameters.fixedCost}
          />
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>AI Insights</h2>
          <InsightsPanel siteId={site.siteId} siteName={site.name} />
        </section>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1rem',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666',
    fontSize: '1.1rem',
  },
  error: {
    textAlign: 'center',
    padding: '2rem',
    color: '#dc3545',
    backgroundColor: '#f8d7da',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  notFound: {
    textAlign: 'center',
    padding: '2rem',
    color: '#666',
  },
  backLink: {
    color: '#0066cc',
    textDecoration: 'none',
    fontSize: '0.9rem',
  },
  header: {
    marginBottom: '2rem',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  exportButtons: {
    display: 'flex',
    gap: '0.5rem',
  },
  exportButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.9rem',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  exportButtonSecondary: {
    padding: '0.5rem 1rem',
    fontSize: '0.9rem',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '1rem',
    marginTop: '0.5rem',
  },
  title: {
    margin: 0,
    fontSize: '1.75rem',
    color: '#333',
  },
  location: {
    color: '#666',
    fontSize: '1rem',
  },
  utilizationBadge: {
    display: 'inline-block',
    marginTop: '0.5rem',
    padding: '0.25rem 0.75rem',
    backgroundColor: '#e9ecef',
    borderRadius: '4px',
    fontSize: '0.9rem',
    color: '#495057',
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  twoColumnRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem',
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1.1rem',
    color: '#333',
    borderBottom: '1px solid #eee',
    paddingBottom: '0.5rem',
  },
};

export default SiteDetail;
